/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useState } from 'react';
import { useStore } from '../../store';
import { ListType, SearchQuery, ValueOf } from '../../types';

type UseSearch = (type?: ValueOf<typeof ListType>) => {
	query: string;
	searchQueryAppVariant: string;
	searchQueryTenant: string;
	searchQueryContract: string;
	onChange: (query: string) => void;
	onSearch: (type: ValueOf<typeof ListType>) => void;
	onReset: (type: ValueOf<typeof ListType>) => void;
};
const useSearch: UseSearch = type => {
	const [query, setQuery] = useState<string>('');
	const { addSearchQuery, removeSearchQuery } = useStore();

	const searchQueryAppVariant = useStore(state => state.searchAppVariant);
	const searchQueryTenant = useStore(state => state.searchTenant);
	const searchQueryContract = useStore(state => state.searchContract);

	useEffect(() => {
		if (type === ListType.APP_VARIANT) setQuery(searchQueryAppVariant);
		if (type === ListType.TENANT) setQuery(searchQueryTenant);
		if (type === ListType.CONTRACT) setQuery(searchQueryContract);
	}, [type, searchQueryAppVariant, searchQueryTenant, searchQueryContract]);

	const onChange = (query: string): void => {
		setQuery(query);
	};

	const onSearch = (type: ValueOf<typeof ListType>): void => {
		const searchQuery: SearchQuery = { query, type };
		addSearchQuery(searchQuery);
	};

	const onReset = (type: ValueOf<typeof ListType>): void => {
		setQuery('');
		removeSearchQuery(type);
	};

	return {
		query,
		searchQueryAppVariant,
		searchQueryTenant,
		searchQueryContract,
		onChange,
		onSearch,
		onReset
	};
};

export default useSearch;
